import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import { BOARD_CENTER } from './constants';
import { sunAt } from './sunCycle';

export interface Lighting {
  /** Fill light from the sky. */
  sky: HemisphericLight;
  sun: DirectionalLight;
  shadows: ShadowGenerator;
  /**
   * The temple's own shadows, its columns and roof on its stone and floor. A copy of the sun lights
   * the temple alone, and the board's sun leaves it, so the board's shadow map spans the board: with
   * the temple in it, the map stretched over eight metres and the pieces' shadows went to smudges.
   * The copy's coarse map is drawn again only as the sun moves on (`TEMPLE_STEP_SECONDS`).
   */
  templeSun: DirectionalLight;
  templeShadows: ShadowGenerator;
  update(seconds: number): void;
}

/** The temple's shadow map: coarse, since a column's shadow is half a metre wide. */
const TEMPLE_MAP_SIZE = 512;
/** The sun turns about a quarter of a degree a second; the temple's shadows follow it in steps. */
const TEMPLE_STEP_SECONDS = 0.5;

/**
 * One sky light for fill and one "sun" that casts the piece shadows onto the board, and a copy of
 * the sun for the temple (`temple.lit`), shadowed by `temple.casters`.
 */
export function createLighting(scene: Scene, temple: { lit: Mesh[]; casters: Mesh[] }): Lighting {
  const sky = new HemisphericLight('skyLight', new Vector3(0, 1, 0), scene);
  sky.intensity = 0.55;
  sky.diffuse = new Color3(0.8, 0.85, 1);
  sky.groundColor = new Color3(0.28, 0.22, 0.4);

  const sun = new DirectionalLight('sun', new Vector3(-0.45, -1, 0.35).normalize(), scene);
  sun.position = BOARD_CENTER.add(new Vector3(1.4, 2.6, -1.1));
  sun.intensity = 1.25;
  sun.diffuse = new Color3(1, 0.95, 0.86);
  sun.shadowMinZ = 0.1;
  sun.shadowMaxZ = 30;

  const shadows = new ShadowGenerator(1024, sun);
  shadows.usePercentageCloserFiltering = true;
  shadows.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
  shadows.bias = 0.0006;
  shadows.normalBias = 0.004;
  shadows.setDarkness(0.35);

  const templeSun = new DirectionalLight('templeSun', sun.direction.clone(), scene);
  templeSun.includedOnlyMeshes = [...temple.lit];
  sun.excludedMeshes = [...temple.lit];
  templeSun.shadowMinZ = sun.shadowMinZ;
  templeSun.shadowMaxZ = sun.shadowMaxZ;
  const templeShadows = new ShadowGenerator(TEMPLE_MAP_SIZE, templeSun);
  templeShadows.usePercentageCloserFiltering = true;
  templeShadows.filteringQuality = ShadowGenerator.QUALITY_LOW;
  templeShadows.bias = 0.002;
  templeShadows.normalBias = 0.02;
  templeShadows.setDarkness(0.35);
  for (const mesh of temple.casters) templeShadows.addShadowCaster(mesh);
  const templeMap = templeShadows.getShadowMap()!;
  templeMap.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
  let templeAt = -Infinity;
  // Written in place every frame: the board's loop allocates nothing it can avoid.
  const toward = new Vector3();

  const update = (seconds: number) => {
    const p = sunAt(seconds);
    toward.set(p.x, p.y, p.z);
    toward.scaleToRef(12, sun.position).addInPlace(BOARD_CENTER);
    toward.negateToRef(sun.direction);
    sun.diffuse.set(1, 0.8 + p.y * 0.16, 0.61 + p.y * 0.32);
    sun.intensity = 0.85 + p.y * 0.4;
    sky.intensity = 0.68;
    templeSun.diffuse.copyFrom(sun.diffuse);
    templeSun.intensity = sun.intensity;
    // The copy turns only with a new map, so its shadows never slide against the light.
    if (Math.abs(seconds - templeAt) >= TEMPLE_STEP_SECONDS) {
      templeAt = seconds;
      templeSun.position.copyFrom(sun.position);
      templeSun.direction.copyFrom(sun.direction);
      templeMap.resetRefreshCounter();
    }
  };
  update(0);
  return { sky, sun, shadows, templeSun, templeShadows, update };
}
